import type { Identity, SearchParams } from './types';

export const AID_KEY = 'vsape_aid';
export const SID_KEY = 'vsape_sid';
const USER_HASH_KEY = 'vsape_user_id_hash';
const SESSION_TOUCHED_KEY = 'vsape_sid_touched_at';
const SEEN_ORDERS_KEY = 'vsape_seen_orders';
const SESSION_IDLE_MS = 30 * 60 * 1000;
const YEAR_SECONDS = 365 * 24 * 60 * 60;
const SEEN_ORDERS_MAX = 20;

export class IdentityStore {
  private identity: Identity | undefined;

  load(now = Date.now()): Identity {
    const anonymous_user_id = selfHealId(AID_KEY, YEAR_SECONDS);
    const session_id = this.loadSession(now);
    const user_id_hash = safeLocalStorageGet(USER_HASH_KEY);
    this.identity = withOptionalHash({ anonymous_user_id, session_id }, user_id_hash);
    return this.identity;
  }

  current(): Identity | undefined {
    return this.identity;
  }

  clearRuntime(): void {
    this.identity = undefined;
  }

  identify(userIdHash: string): void {
    if (!this.identity || !isNonEmpty(userIdHash)) return;
    safeLocalStorageSet(USER_HASH_KEY, userIdHash);
    this.identity = { ...this.identity, user_id_hash: userIdHash };
  }

  getSearchParams(): SearchParams {
    if (!this.identity) {
      return { session_id: '', anonymous_user_id: '' };
    }
    return withOptionalHash(
      {
        session_id: this.identity.session_id,
        anonymous_user_id: this.identity.anonymous_user_id
      },
      this.identity.user_id_hash
    );
  }

  hasSeenOrder(orderId: string): boolean {
    return this.loadSeenOrders().includes(orderId);
  }

  markOrderSeen(orderId: string): void {
    const orders = this.loadSeenOrders().filter((id) => id !== orderId);
    orders.push(orderId);
    if (orders.length > SEEN_ORDERS_MAX) orders.splice(0, orders.length - SEEN_ORDERS_MAX);
    try {
      if (typeof localStorage !== 'undefined') {
        if (this.identity) {
          localStorage.setItem(
            SEEN_ORDERS_KEY,
            JSON.stringify({ session_id: this.identity.session_id, order_ids: orders })
          );
        }
      }
    } catch {
      // localStorage unavailable in private browsing
    }
  }

  private loadSeenOrders(): string[] {
    const raw = safeLocalStorageGet(SEEN_ORDERS_KEY);
    if (!raw) return [];
    try {
      const parsed: unknown = JSON.parse(raw);
      if (!this.identity || !isRecord(parsed) || parsed.session_id !== this.identity.session_id || !Array.isArray(parsed.order_ids)) {
        return [];
      }
      return parsed.order_ids.filter((id): id is string => typeof id === 'string').slice(-SEEN_ORDERS_MAX);
    } catch {
      return [];
    }
  }

  private loadSession(now: number): string {
    const touched = Number(safeLocalStorageGet(SESSION_TOUCHED_KEY) || '0');
    const expired = !Number.isFinite(touched) || now - touched > SESSION_IDLE_MS;
    const sessionId = expired ? createId() : selfHealId(SID_KEY);

    if (expired) {
      persistId(SID_KEY, sessionId);
    }
    safeLocalStorageSet(SESSION_TOUCHED_KEY, String(now));
    return sessionId;
  }
}

function selfHealId(name: string, maxAgeSeconds?: number): string {
  const cookieValue = readCookie(name);
  const storageValue = safeLocalStorageGet(name);
  const value = cookieValue || storageValue || createId();
  persistId(name, value, maxAgeSeconds);
  return value;
}

function persistId(name: string, value: string, maxAgeSeconds?: number): void {
  writeCookie(name, value, maxAgeSeconds);
  safeLocalStorageSet(name, value);
}

function writeCookie(name: string, value: string, maxAgeSeconds?: number): void {
  if (typeof document === 'undefined') return;
  const maxAge = maxAgeSeconds ? `; Max-Age=${maxAgeSeconds}` : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; SameSite=Lax${maxAge}`;
}

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const prefix = `${name}=`;
  const part = document.cookie
    .split(';')
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(prefix));
  if (!part) return undefined;
  try {
    return decodeURIComponent(part.slice(prefix.length));
  } catch {
    return undefined;
  }
}

function safeLocalStorageGet(key: string): string | undefined {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage.getItem(key) || undefined;
  } catch {
    return undefined;
  }
}

function safeLocalStorageSet(key: string, value: string): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(key, value);
  } catch {
    // Storage can be unavailable in private browsing modes.
  }
}

function createId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  const byte6 = bytes[6];
  const byte8 = bytes[8];
  if (byte6 === undefined || byte8 === undefined) return `${Date.now()}-${Math.random()}`;
  bytes[6] = (byte6 & 0x0f) | 0x40;
  bytes[8] = (byte8 & 0x3f) | 0x80;
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10).join('')}`;
}

function isNonEmpty(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function withOptionalHash(
  value: { session_id: string; anonymous_user_id: string },
  hash: string | undefined
): Identity {
  return hash ? { ...value, user_id_hash: hash } : value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
